import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import GiftIconSvg from '../../../assets/icons/icon-home-dashboard-gift.svg?react';
import {
  type EmployeeTipStats,
  getEmployeeDashboard,
  getEmployeeProfile,
} from '../../../shared/api/employee.service';
import { useEmployeeSession } from '../../../shared/hooks/use-employee-session';
import { resolveEmployeeSessionCountryCode } from '../../../shared/model';
import { resolveDashboardPositiveAmountParts } from '../model';

import { HomeDashboardStatementCard } from './home-dashboard-statement-card';

const SummaryGrid = styled.div.attrs({
  'data-ui': 'home-dashboard-summary-cards',
})`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${({ theme }) => theme.scale.px(10)};
`;

const Heading = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(16)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(19)};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Value = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(24)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(28)};
`;

const PositiveValue = styled.span`
  color: ${({ theme }) => theme.colors.support.success};
`;

const MultiplierRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.scale.px(8)};
`;

const GiftIcon = styled(GiftIconSvg).attrs({ 'aria-hidden': true })`
  width: ${({ theme }) => theme.scale.px(29)};
  height: ${({ theme }) => theme.scale.px(29)};
  --stroke-0: ${({ theme }) => theme.colors.text.primary};
`;

const Multiplier = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(24)};
  line-height: ${({ theme }) => theme.scale.px(24)};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Card = styled.article`
  border-radius: ${({ theme }) => theme.scale.px(10)};
  background: ${({ theme }) => theme.colors.surface.subtle};
  padding: ${({ theme }) => theme.scale.px(13)};
  box-sizing: border-box;
  display: grid;
  align-content: start;

  & > ${Heading} + ${Value} {
    margin-top: ${({ theme }) => theme.scale.px(13)};
  }

  & > ${Value} + ${MultiplierRow} {
    margin-top: ${({ theme }) => theme.scale.px(11)};
  }
`;

type HomeDashboardSummaryCardsProps = {
  onOpenStatement?: () => void;
};

function resolveEmployeeDashboardTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  } catch {
    return '';
  }
}

export function HomeDashboardSummaryCards({
  onOpenStatement,
}: HomeDashboardSummaryCardsProps) {
  const { t } = useTranslation();
  const { authContact, status } = useEmployeeSession();
  const profileQuery = useQuery({
    enabled: status === 'authenticated',
    queryFn: getEmployeeProfile,
    queryKey: ['employee', 'profile'],
  });
  const dashboardQuery = useQuery({
    enabled: status === 'authenticated',
    queryFn: () => getEmployeeDashboard(resolveEmployeeDashboardTimeZone()),
    queryKey: ['employee', 'dashboard'],
  });
  const profile = profileQuery.data ?? null;
  const tipStats: EmployeeTipStats | null =
    dashboardQuery.data?.tipStats ?? null;
  const countryCode = resolveEmployeeSessionCountryCode(profile, authContact);
  const receivedTodayAmount = resolveDashboardPositiveAmountParts(
    tipStats?.depositSumDay,
    countryCode,
  );
  const receivedTodayMultiplier =
    typeof tipStats?.depositCountDay === 'number' &&
    tipStats.depositCountDay >= 0
      ? `x${tipStats.depositCountDay}`
      : 'x0';

  return (
    <SummaryGrid>
      <Card>
        <Heading>{t('homeDashboard.receivedToday')}</Heading>
        <Value>
          {receivedTodayAmount.prefix}
          <PositiveValue>{receivedTodayAmount.value}</PositiveValue>
        </Value>
        <MultiplierRow>
          <GiftIcon />
          <Multiplier>{receivedTodayMultiplier}</Multiplier>
        </MultiplierRow>
      </Card>

      <HomeDashboardStatementCard
        countryCode={countryCode}
        onOpenStatement={onOpenStatement}
        tipStats={tipStats}
      />
    </SummaryGrid>
  );
}
